// src/components/DefenseDeck.js
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../index.css";
import { getSkinSrc } from "./SeasonalSkins";
import CardPicker from "./CardPicker";
import LoadingSpinner from "./LoadingSpinner";

const API_BASE = process.env.REACT_APP_API_URL;

const OVERALL_MODE = true;
const OVERALL_DECK_KEY = "overall";

const RARITIES = [
  { key: "common", label: "Common" },
  { key: "rare", label: "Rare" },
  { key: "epic", label: "Epic" },
  { key: "legendary", label: "Legendary" },
];

const sortOptions = [
  { value: "power", label: "Sort: Power" },
  { value: "level", label: "Sort: Level" },
  { value: "attack", label: "Sort: Attack" },
  { value: "health", label: "Sort: Health" },
];

const FALLBACK_DECK_SIZE = 3;

const EMPTY_DEFENSE_STATS = {
  totalBattles: 0,
  totalWins: 0,
  totalLosses: 0,
  totalWinRate: 0,
  todayBattles: 0,
  todayWins: 0,
  todayLosses: 0,
  todayWinRate: 0,
};

function normalizeRarity(rarity) {
  const r = String(rarity || "common").trim().toLowerCase();

  if (r === "legendary") return "legendary";
  if (r === "epic") return "epic";
  if (r === "rare") return "rare";
  return "common";
}

function rarityLabel(rarity) {
  const r = normalizeRarity(rarity);

  if (r === "legendary") return "Legendary";
  if (r === "epic") return "Epic";
  if (r === "rare") return "Rare";
  return "Common";
}

function rarityRank(rarity) {
  const r = normalizeRarity(rarity);

  if (r === "legendary") return 4;
  if (r === "epic") return 3;
  if (r === "rare") return 2;
  return 1;
}

function safeParseJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function parseAbilities(raw) {
  const parsed = safeParseJson(raw, []);
  return Array.isArray(parsed) ? parsed : [];
}

function sanitizeAbilityList(list) {
  return (Array.isArray(list) ? list : [])
    .map((ability) => {
      if (typeof ability === "string") return ability;
      return ability?.name || "";
    })
    .map((ability) => String(ability || "").trim())
    .filter(Boolean);
}

function getCardPiecesTotal(card) {
  return (
    Number(
      card?.pieces ??
        card?.card_pieces ??
        card?.cardPieces ??
        card?.fragments ??
        0
    ) || 0
  );
}

function getCardCharacter(card) {
  const parsed = safeParseJson(card?.character, {});
  return parsed && typeof parsed === "object" ? parsed : {};
}

function cardPower(card) {
  return (
    Number(card?.attack || 0) +
    Number(card?.defense || 0) +
    Number(card?.health || 0) +
    Number(card?.level || 1) * 2 +
    rarityRank(card?.rarity) * 5
  );
}

function isInvalidDefenseCard(card, activeRarity) {
  return (
    (!OVERALL_MODE &&
      normalizeRarity(card?.rarity) !== normalizeRarity(activeRarity)) ||
    !!card?.for_sale ||
    !!card?.is_consumed ||
    !!card?.merged_into_card_id
  );
}

function getErrorMessage(error, deckSize) {
  switch (error) {
    case "INVALID_RARITY":
      return "Invalid rarity.";
    case "INVALID_DECK_SIZE":
      return `Select 1 to ${deckSize} cards.`;
    case "DUPLICATE_CARDS":
      return "You cannot use the same card twice.";
    case "CARD_NOT_FOUND":
      return "One of these cards could not be found.";
    case "NOT_YOUR_CARD":
      return "One of these cards does not belong to you.";
    case "CARD_NOT_IN_ACTIVE_SEASON":
      return "Only active season cards can be used.";
    case "CARD_RARITY_MISMATCH":
      return "All cards must match the selected rarity.";
    case "CARD_FOR_SALE":
      return "Cards listed for sale cannot be used.";
    case "CARD_ALREADY_MERGED":
      return "Merged/consumed cards cannot be used.";
    case "CARD_HAS_PENDING_BATTLE":
      return "One of these cards has a pending battle.";
    case "USER_SIDE_NOT_SELECTED":
      return "You need to choose a side before setting a Defense Deck.";
    case "CARD_SIDE_MISMATCH":
      return "One of these cards does not match your side.";
    case "DEFENDER_SIDE_NOT_SELECTED":
      return "This defender has not selected a side yet.";
    case "FAILED_TO_SAVE_DEFENSE_DECK":
      return "Could not save your Defense Deck.";
    case "FAILED_TO_LOAD_DEFENSE_DECK":
      return "Could not load your saved Defense Deck.";
    case "FAILED_TO_LOAD_DEFENSE_DECKS":
      return "Could not load your Defense Decks.";
    default:
      return error || "Something went wrong.";
  }
}

function getLogResult(log) {
  return String(log?.defender?.result || log?.defender_result || "").toLowerCase();
}

function getLogDateLabel(value) {
  if (!value) return "";

  try {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return "";

    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function truncateCardName(name, max = 20) {
  const text = String(name || "").trim();

  if (!text) return "Card";
  if (text.length <= max) return text;

  return `${text.slice(0, max)}…`;
}

function getAttackerCardName(log) {
  return (
    log?.attacker?.cardName ||
    log?.attacker_card_name ||
    "Attack Card"
  );
}

function getDefenseBattleLabel(log) {
  const attackerCard = truncateCardName(getAttackerCardName(log), 20);
  const defenderCard = truncateCardName(getDefenderCardName(log), 20);

  return `${attackerCard} x ${defenderCard}`;
}

function getAttackerName(log) {
  return log?.attacker?.username || log?.attacker_username || "Unknown";
}

function getDefenderCardName(log) {
  return log?.defender?.cardName || log?.defender_card_name || "Defense Card";
}

function normalizeDefenseStats(summary) {
  if (!summary || typeof summary !== "object") return EMPTY_DEFENSE_STATS;

  return {
    totalBattles: Number(summary.totalBattles || 0),
    totalWins: Number(summary.totalWins || 0),
    totalLosses: Number(summary.totalLosses || 0),
    totalWinRate: Number(summary.totalWinRate || 0),
    todayBattles: Number(summary.todayBattles || 0),
    todayWins: Number(summary.todayWins || 0),
    todayLosses: Number(summary.todayLosses || 0),
    todayWinRate: Number(summary.todayWinRate || 0),
  };
}

function CardTile({ card, selected, disabled, selectedIndex, onClick }) {
  const abilitiesSafe = sanitizeAbilityList(parseAbilities(card?.abilities));
  const totalCardPieces = getCardPiecesTotal(card);
  const character = getCardCharacter(card);

  return (
    <div
      className="card-item"
      style={{
        opacity: disabled ? 0.45 : 1,
        boxShadow: selected ? "0 0 0 3px #ffdf5f" : undefined,
        borderRadius: 12,
      }}
    >
      <div className="card-title">
        {card.name && card.name.length > 18
          ? card.name.slice(0, 16) + "…"
          : card.name || `Card #${card.id}`}
      </div>

      <div
        className="character-preview"
        onClick={() => {
          if (!disabled) onClick?.(card);
        }}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 34,
            right: 8,
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: selected ? "#ffdf5f" : "rgba(0,0,0,0.45)",
            border: "2px solid #fff",
            color: selected ? "#2b1400" : "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            zIndex: 20,
            fontSize: 16,
          }}
        >
          {selected ? selectedIndex + 1 : ""}
        </div>

        <div
          className="card-number"
          style={{
            backgroundImage: "url('/number.png')",
            backgroundSize: "36px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
          }}
        >
          {card.level ?? 1}
        </div>

        <div
          style={{
            position: "absolute",
            top: 38,
            left: 8,
            height: 24,
            padding: "0 8px 0 24px",
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.72)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            textShadow: "0 1px 2px #000",
            backgroundImage: "url('/pieces.png')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left 6px center",
            backgroundSize: "14px 14px",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          {totalCardPieces}
        </div>

        {card.for_sale && <div className="for-sale-badge">FOR SALE</div>}

        <div
          className="card-type"
          style={{
            backgroundImage: "url('/arrow2.png'), url('/card-type.png')",
            backgroundSize: "10px 10px,100% 24px",
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundPosition: "left 6px top 6px, left center",
          }}
        >
          {card.rarity}
        </div>

        <div className="card-stats">
          <div
            style={{
              backgroundImage: "url('/s_attack.png')",
              backgroundSize: "15px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
            }}
          >
            {card.attack}
          </div>

          <div
            style={{
              paddingLeft: "18px",
              backgroundImage: "url('/s_defense.png')",
              backgroundSize: "15px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
            }}
          >
            {card.defense}
          </div>

          <div
            style={{
              paddingLeft: "18px",
              backgroundImage: "url('/s_health.png')",
              backgroundSize: "15px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
            }}
          >
            {card.health}
          </div>
        </div>

        {abilitiesSafe.map((ability, idx) => {
          const abilityKey = String(ability || "").toLowerCase();

          return (
            <div
              key={`${ability}-${idx}`}
              className={`abl_${idx + 1}`}
              style={{
                width: "32px",
                height: "32px",
                backgroundImage: "url('/abl.png')",
                backgroundSize: "32px",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 30,
                position: "absolute",
                borderRadius: "50%",
                pointerEvents: "none",
              }}
              title={ability}
            >
              <img
                src={`/a_${abilityKey}.png`}
                alt={ability}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                style={{
                  width: "24px",
                  height: "24px",
                  pointerEvents: "none",
                }}
              />
            </div>
          );
        })}

        <div className="card-thumb" style={{ position: "relative" }}>
          <div
            className="char_bg"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
            }}
          >
            {Object.entries(character)
              .sort(([a], [b]) => {
                const order = [
                  "background",
                  "body",
                  "boots",
                  "pants",
                  "shirt",
                  "neck",
                  "face",
                  "eyes",
                  "mouth",
                  "head",
                  "weapons",
                ];

                const indexA = order.includes(a)
                  ? order.indexOf(a)
                  : order.length;
                const indexB = order.includes(b)
                  ? order.indexOf(b)
                  : order.length;

                return indexA - indexB;
              })
              .map(([part, data]) => {
                if (!data || typeof data !== "object") return null;

                const defaultSrc = `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

                const seasonName =
                  card?.seasonal_accessory?.season_name ||
                  card?.season_name ||
                  null;

                const src = getSkinSrc(seasonName, part, defaultSrc);

                const zIndexMap = {
                  background: 1,
                  body: 2,
                  boots: 3,
                  pants: 4,
                  shirt: 5,
                  neck: 6,
                  face: 7,
                  eyes: 8,
                  mouth: 9,
                  head: 10,
                  weapons: 11,
                };

                return (
                  <img
                    key={part}
                    src={src}
                    alt={part}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      zIndex: zIndexMap[part] ?? 10,
                      pointerEvents: "none",
                    }}
                  />
                );
              })}
          </div>
        </div>

        {disabled && (
          <div
            style={{
              position: "absolute",
              left: 8,
              right: 8,
              bottom: 8,
              zIndex: 50,
              borderRadius: 10,
              padding: "5px 8px",
              background: "rgba(0,0,0,0.75)",
              color: "#ff8a8a",
              fontSize: 12,
              textAlign: "center",
              textShadow: "1px 1px #000",
              pointerEvents: "none",
            }}
          >
            Not eligible
          </div>
        )}
      </div>
    </div>
  );
}

function BattleSquadSlot({ card, slotIndex, onClick, onRemove }) {
  const character = getCardCharacter(card);
  const abilitiesSafe = sanitizeAbilityList(parseAbilities(card?.abilities));

  if (!card) {
    return (
      <div
        style={{
          aspectRatio: 0.8,
          backgroundImage: 'url("/v2/nocard.png"), url("/v2/item.png")',
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "55%, 100% 100%",
          backgroundPosition: "center center, center center",
          padding: 0,
          paddingBottom: 6,
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <button onClick={() => onClick?.(slotIndex)} className="game-button-small yellow-button">
          SELECT
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundImage: 'url("/v2/item.png")',
        padding: 3,
        backgroundRepeat: "repeat",
        backgroundSize: "100% 100%",
        backgroundPosition: "center center",
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >

    <button
  type="button"
  onClick={(e) => {
    e.stopPropagation();
    onRemove?.(slotIndex);
  }}
  style={{
    position: "absolute",
    top: 6,
    right: 5,
    zIndex: 80,
    width: 26,
    height: 26,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
  }}
>
  <img
    src="/v2/close.png"
    alt="Remove"
    style={{
      width: "100%",
      height: "100%",
      display: "block",
      pointerEvents: "none",
    }}
  />
</button>

      <div
        style={{
          float: "left",
          width: "100%",
          cursor: "default",
          position: "relative",
          borderRadius: 4
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 6,
            right: 5,
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            pointerEvents: "none",
          }}
        >
          {abilitiesSafe.map((ability, idx) => {
            const abilityKey = String(ability || "").toLowerCase();

            return (
              <div
                key={`${ability}-${idx}`}
                style={{
                  width: 15,
                  height: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={ability}
              >
                <img
                  src={`/a_${abilityKey}.png`}
                  alt={ability}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    width: 15,
                    height: 15,
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              </div>
            );
          })}
        </div>

        <div
          style={{
            position: "absolute",
            top: 3,
            left: 3,
            width: 28,
            height: 28,
            backgroundImage: "url('/v2/position.png')",
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            color: "#fff",
            fontSize: 15,
            lineHeight: "28px",
            textAlign: "center",
            zIndex: 20,
            fontFamily: '"Fredoka", sans-serif',
            fontWeight: 600,
          }}
        >
          {card.level ?? 1}
        </div>

        <div
          style={{
            zIndex: 20,
            textTransform: "uppercase",
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            color: "#fff",
            height: 18,
            fontSize: 11,
            lineHeight: "20px",
            textAlign: "center",
            fontWeight: 600,
            background: "rgba(77, 44, 36, 0.5)",
          }}
        >
          {card.rarity || "common"}
        </div>

        <div
          className="char_bg"
          style={{
            borderRadius: 4,
            overflow: "hidden",
            position: "relative",
            width: "100%",
            aspectRatio: 1,
            zIndex: 2,
          }}
        >
          {Object.entries(character)
            .sort(([a], [b]) => {
              const order = [
                "background",
                "body",
                "boots",
                "pants",
                "shirt",
                "neck",
                "face",
                "eyes",
                "mouth",
                "head",
                "weapons",
              ];

              const indexA = order.includes(a) ? order.indexOf(a) : order.length;
              const indexB = order.includes(b) ? order.indexOf(b) : order.length;

              return indexA - indexB;
            })
            .map(([part, data]) => {
              if (!data || typeof data !== "object") return null;

              const defaultSrc = `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

              const src = getSkinSrc(
                card?.seasonal_accessory?.season_name || card?.season_name || null,
                part,
                defaultSrc
              );

              const zIndexMap = {
                background: 1,
                body: 2,
                boots: 3,
                pants: 4,
                shirt: 5,
                neck: 6,
                face: 7,
                eyes: 8,
                mouth: 9,
                head: 10,
                weapons: 11,
              };

              return (
                <img
                  key={part}
                  src={src}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    zIndex: zIndexMap[part] ?? 10,
                    pointerEvents: "none",
                  }}
                />
              );
            })}
        </div>
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
          padding: "3px 4px",
          boxSizing: "border-box",
          height: 24
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#fff",
            fontSize: 12,
            fontWeight: 500,
            textShadow: "1px 1px #000",
          }}
        >
          <img src="/s_attack.png" alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />
          {card.attack ?? 0}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#fff",
            fontSize: 12,
            fontWeight: 500,
            textShadow: "1px 1px #000",
          }}
        >
          <img src="/s_defense.png" alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />
          {card.defense ?? 0}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#fff",
            fontSize: 12,
            fontWeight: 500,
            textShadow: "1px 1px #000",
          }}
        >
          <img src="/s_health.png" alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />
          {card.health ?? 0}
        </div>
      </div>
    </div>
  );
}

export default function DefenseDeck({
  onNavigate,
  onDeckSaved,
  openedVia = null,
  returnTo = null,
  arenaId = null,
  arenaIndex = null,
}) {
  const [deckSize, setDeckSize] = useState(FALLBACK_DECK_SIZE);
  const [activeRarity, setActiveRarity] = useState("common");

  const [cards, setCards] = useState([]);
  const [savedDecks, setSavedDecks] = useState({
    overall: [],
    common: [],
    rare: [],
    epic: [],
    legendary: [],
  });

  const [selectedByRarity, setSelectedByRarity] = useState({
    overall: [],
    common: [],
    rare: [],
    epic: [],
    legendary: [],
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [cardPickerOpen, setCardPickerOpen] = useState(false);
  const [cardPickerSlotIndex, setCardPickerSlotIndex] = useState(null);

  const [defenseLogs, setDefenseLogs] = useState([]);
  const [defenseStats, setDefenseStats] = useState(EMPTY_DEFENSE_STATS);
  const [defenseLogsLoading, setDefenseLogsLoading] = useState(false);
  const [defenseLogsError, setDefenseLogsError] = useState("");
  const [defenseStatsLoaded, setDefenseStatsLoaded] = useState(false);

  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const [sortKey, setSortKey] = useState("power");
  const [view, setView] = useState("deck");

  const activeDeckKey = OVERALL_MODE ? OVERALL_DECK_KEY : activeRarity;
  const activeSelectedIds = selectedByRarity[activeDeckKey] || [];
  const activeSavedIds = savedDecks[activeDeckKey] || [];

  const selectedCards = useMemo(() => {
    return activeSelectedIds
      .map((id) => cards.find((card) => Number(card.id) === Number(id)))
      .filter(Boolean);
  }, [activeSelectedIds, cards]);

  const activeCards = useMemo(() => {
    const list = OVERALL_MODE
      ? [...cards]
      : cards.filter(
          (card) => normalizeRarity(card?.rarity) === activeRarity
        );

    list.sort((a, b) => {
      if (sortKey === "level") {
        return Number(b.level || 1) - Number(a.level || 1);
      }

      if (sortKey === "attack") {
        return Number(b.attack || 0) - Number(a.attack || 0);
      }

      if (sortKey === "health") {
        return Number(b.health || 0) - Number(a.health || 0);
      }

      return cardPower(b) - cardPower(a);
    });

    return list;
  }, [cards, activeRarity, sortKey]);

  const isDirty = useMemo(() => {
    const a = [...activeSavedIds].map(Number).sort((x, y) => x - y);
    const b = [...activeSelectedIds].map(Number).sort((x, y) => x - y);

    return JSON.stringify(a) !== JSON.stringify(b);
  }, [activeSavedIds, activeSelectedIds]);

  useEffect(() => {
  let cancelled = false;

  async function load() {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Not logged in.");

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
      };

      const [cardsRes, decksRes, configRes] = await Promise.all([
        fetch(`${API_BASE}/api/shop/mine`, { headers }),
        fetch(`${API_BASE}/api/pvp/defense-decks`, { headers }),
        fetch(`${API_BASE}/api/pvp/config`, { headers }),
      ]);

      const cardsJson = await cardsRes.json().catch(() => ({}));
      const decksJson = await decksRes.json().catch(() => ({}));
      const configJson = await configRes.json().catch(() => ({}));

      if (!cardsRes.ok) {
        throw new Error(cardsJson.error || "Failed to load cards.");
      }

      if (!decksRes.ok) {
        throw new Error(getErrorMessage(decksJson.error, FALLBACK_DECK_SIZE));
      }

      const nextDeckSize =
        Number(configJson?.defenseDeckSize) > 0
          ? Number(configJson.defenseDeckSize)
          : Number(decksJson?.defenseDeckSize) > 0
            ? Number(decksJson.defenseDeckSize)
            : FALLBACK_DECK_SIZE;

      const allCards = Array.isArray(cardsJson.cards) ? cardsJson.cards : [];

      const nextSaved = {
        overall: [],
        common: [],
        rare: [],
        epic: [],
        legendary: [],
      };

      if (OVERALL_MODE) {
        const cardIds = decksJson?.decks?.overall?.cardIds;

        nextSaved.overall = Array.isArray(cardIds)
          ? cardIds.map(Number)
          : [];
      } else {
        for (const rarity of RARITIES) {
          const cardIds = decksJson?.decks?.[rarity.key]?.cardIds;

          nextSaved[rarity.key] = Array.isArray(cardIds)
            ? cardIds.map(Number)
            : [];
        }
      }

      if (!cancelled) {
        setDeckSize(nextDeckSize);
        setCards(allCards);
        setSavedDecks(nextSaved);
        setSelectedByRarity(nextSaved);
      }
    } catch (err) {
      if (!cancelled) {
        setError(err.message || "Load error.");
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  }

  load();

  return () => {
    cancelled = true;
  };
}, []);


const loadDefenseStats = async () => {
  if (defenseStatsLoaded || defenseLogsLoading) return;

  try {
    setDefenseLogsLoading(true);
    setDefenseLogsError("");

    const token = localStorage.getItem("token");
    if (!token) throw new Error("Not logged in.");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    };

    const logsRes = await fetch(
      `${API_BASE}/api/pvp/offline-battle-logs?limit=20`,
      { headers }
    );

    const logsJson = await logsRes.json().catch(() => ({}));

    if (!logsRes.ok) {
      throw new Error("Could not load defense battle stats.");
    }

    setDefenseLogs(Array.isArray(logsJson.logs) ? logsJson.logs : []);
    setDefenseStats(
      logsJson.summary
        ? normalizeDefenseStats(logsJson.summary)
        : EMPTY_DEFENSE_STATS
    );
    setDefenseStatsLoaded(true);
  } catch (err) {
    setDefenseLogsError(
      err.message || "Could not load defense battle stats."
    );
  } finally {
    setDefenseLogsLoading(false);
  }
};

  const setActiveSelection = (updater) => {
    setSelectedByRarity((prev) => {
      const current = prev[activeDeckKey] || [];
      const next = typeof updater === "function" ? updater(current) : updater;

      return {
        ...prev,
        [activeDeckKey]: next,
      };
    });
  };

  const openCardPickerForSlot = (slotIndex) => {
  setCardPickerSlotIndex(slotIndex);
  setCardPickerOpen(true);
};

const closeCardPicker = () => {
  setCardPickerOpen(false);
  setCardPickerSlotIndex(null);
};

const handlePickedDefenseCard = ({ card }) => {
  if (!card || isInvalidDefenseCard(card, activeRarity)) {
    setError("This card cannot be used in your Defense Deck.");
    return;
  }

  const cardId = Number(card.id);

  setCards((prev) => {
    const exists = prev.some((c) => Number(c.id) === cardId);

    if (exists) {
      return prev.map((c) => (Number(c.id) === cardId ? { ...c, ...card } : c));
    }

    return [...prev, card];
  });

  setActiveSelection((prev) => {
    const next = [...prev];
    const slotIndex =
      Number.isInteger(cardPickerSlotIndex) && cardPickerSlotIndex >= 0
        ? cardPickerSlotIndex
        : next.length;

    const alreadyUsedIndex = next.findIndex((id) => Number(id) === cardId);

    if (alreadyUsedIndex !== -1) {
      next[alreadyUsedIndex] = next[slotIndex];
    }

    next[slotIndex] = cardId;

    return next
      .filter((id) => id != null)
      .slice(0, deckSize);
  });

  setError("");
  closeCardPicker();
};

const removeCardFromSlot = (slotIndex) => {
  setActiveSelection((prev) => {
    const next = [...prev];
    next.splice(slotIndex, 1);
    return next;
  });

  setError("");
};

  const selectCard = (card) => {
    if (!card || isInvalidDefenseCard(card, activeRarity)) return;

    setError("");

    setActiveSelection((prev) => {
      const cardId = Number(card.id);

      if (prev.includes(cardId)) {
        return prev.filter((id) => Number(id) !== cardId);
      }

      if (prev.length >= deckSize) {
        return prev;
      }

      return [...prev, cardId];
    });
  };

  const clearSelection = () => {
    setActiveSelection([]);
    setError("");
  };

  const autoPickBest = () => {
    const best = activeCards
      .filter((card) => !isInvalidDefenseCard(card, activeRarity))
      .sort((a, b) => cardPower(b) - cardPower(a))
      .slice(0, deckSize)
      .map((card) => Number(card.id));

    setActiveSelection(best);
    setError("");
  };

  const saveDeck = async () => {
    if (
      activeSelectedIds.length < 1 ||
      activeSelectedIds.length > deckSize ||
      saving
    ) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Not logged in.");

      const res = await fetch(
        `${API_BASE}/api/pvp/defense-deck/${activeDeckKey}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
          },
          body: JSON.stringify({
            cardIds: activeSelectedIds,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(getErrorMessage(data?.error, deckSize));
      }

      const nextIds = Array.isArray(data.cardIds)
        ? data.cardIds.map(Number)
        : activeSelectedIds;

      setSavedDecks((prev) => ({
        ...prev,
        [activeDeckKey]: nextIds,
      }));

      setSelectedByRarity((prev) => ({
        ...prev,
        [activeDeckKey]: nextIds,
      }));

      setSuccessOpen(true);
      onDeckSaved?.({
  deckKey: activeDeckKey,
  cardIds: nextIds,
});
    } catch (err) {
      setError(err.message || "Failed to save Defense Deck.");
    } finally {
      setSaving(false);
    }
  };

  const clearSavedDeck = async () => {
    if (saving) return;

    try {
      setSaving(true);
      setError("");

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Not logged in.");

      const res = await fetch(
        `${API_BASE}/api/pvp/defense-deck/${activeDeckKey}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(getErrorMessage(data?.error, deckSize));
      }

      setSavedDecks((prev) => ({
        ...prev,
        [activeDeckKey]: [],
      }));

      setSelectedByRarity((prev) => ({
        ...prev,
        [activeDeckKey]: [],
      }));
    } catch (err) {
      setError(err.message || "Failed to clear Defense Deck.");
    } finally {
      setSaving(false);
    }
  };

  const returnAfterDefenseDeck = () => {
  if (
    openedVia === "arena" ||
    returnTo === "arenas"
  ) {
    onNavigate?.("arenas", {
      arenaId,
      arenaIndex:
        Number.isInteger(arenaIndex)
          ? arenaIndex
          : undefined,
    });

    return;
  }

  onNavigate?.("inventoryHub");
};

 return (
  <div className="modal_box">

  <div className="modal_title">Offline Defense Deck

<button
  type="button"
  className="modal_close"
  onClick={returnAfterDefenseDeck}
/>

  </div>

    <motion.div className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

    {loading ? (
    <div className="modal_inside_2">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingSpinner text="Loading Defense Deck" size={58} />
      </div>
    </div>
  ) : (
    <>
        
        

        {view === "stats" && (
  <>
          <div className="modal_inside_2">
            
              <div
              className="shadow2"
                style={{
                  textAlign: "center",
                  color: "#ffdf5f",
                  fontSize: 21,
                  fontWeight: 600,
                  marginBottom: 5
                }}
              >
                Battle Squad Report
              </div>

            


            <div className="info">

            

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 7,
              }}
            >
            <div
  style={{
    gridColumn: "1 / -1",
    width: "100%",
    display: "grid",
    gridTemplateColumns: "30% 70%",
    alignItems: "center",
    gap: 8,
    boxSizing: "border-box",
  }}
>
  <div style={{textAlign: "center",position: "relative"}}>

    <div style={{top: 0, left: "calc(50% - 21px)", position: "absolute",lineHeight: "42px", width: 42, height: 42, borderRadius: "50%", backgroundColor: "#30d219"}}>
      {defenseStats.totalWinRate}%
    </div>
    <div style={{textAlign: "center", fontSize: 14, bottom: 0, left: 0, width: "100%", position: "absolute"}}>
      Win Rate
    </div>
    <img
      src="/v2/winrate.png"
      alt="Win rate"
      style={{
        width: "100%",
        height: "auto",
        display: "block",
      }}
    />
  </div>

  <div
  className="shadow1"
    style={{
      fontWeight: 600,
      fontSize: 21,
      textAlign: "center",
    }}
  >
    {defenseLogs.length} Recent

 <div
              style={{
                fontSize: 14,
                marginBottom: 10
              }}
            >
              Today: {defenseStats.todayWins}W/{defenseStats.todayLosses}L -
              All: {defenseStats.totalWins}W/{defenseStats.totalLosses}L
            </div>

  </div>
</div>

              <div
                style={{
                  borderRadius: 6,
                  padding: "8px 5px",
                  background: "rgba(255,255,255,0.07)",
                  textAlign: "center",
                }}
              >
              <div style={{ color: "#fff", fontSize: 15 }}>
                  Today
                </div>
                <div
                className="shadow1"
                  style={{
                    color: "#ffe100",
                    fontSize: 21,
                  }}
                >
                  {defenseStats.todayBattles}
                </div>                
              </div>

              <div
                style={{
                  borderRadius: 12,
                  padding: "8px 5px",
                  background: "rgba(255,255,255,0.07)",
                  textAlign: "center",
                }}
              >
              <div style={{ color: "#fff", fontSize: 15 }}>
                  Wins
                </div>
                <div
                className="shadow1"
                  style={{
                    color: "#ffe100",
                    fontSize: 21,
                  }}
                >
                  {defenseStats.totalWins}
                </div>
              </div>

              <div
                style={{
                  borderRadius: 12,
                  padding: "8px 5px",
                  background: "rgba(255,255,255,0.07)",
                  textAlign: "center",
                }}
              >
               <div style={{ color: "#fff", fontSize: 15 }}>
                  Losses
                </div>
                <div
                className="shadow1"
                  style={{
                    color: "#ffe100",
                    fontSize: 21,
                  }}
                >
                  {defenseStats.totalLosses}
                </div>
              </div>

              

              </div>

            </div>

            <div className="shadow1" style={{float: "left", width: "100%", marginTop: 20,fontWeight: 600, fontSize: 21}}>Battle Statistics</div>

            {defenseLogsError && (
              <div
                style={{
                  color: "#ff8a8a",
                  fontSize: 13,
                  textAlign: "center",
                  marginTop: 8,
                }}
              >
                {defenseLogsError}
              </div>
            )}


            {defenseLogs.length > 0 && (

              <div className="info" style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 7 }}>
                {defenseLogs.slice(0, 20).map((log) => {
                  const result = getLogResult(log);
                  const won = result === "win";

                  return (
                    <div
                      key={
                        log.battleId ||
                        `${log.createdAt}-${getDefenderCardName(log)}`
                      }
                      style={{
                        display: "grid",
                        gridTemplateColumns: "38px 1fr auto",
                        gap: 8,
                        alignItems: "center",
                        borderRadius: 12,
                        padding: "8px 9px",
                        background: "rgba(0,0,0,0.23)",
                        border: "1px solid rgba(255,255,255,0.09)",
                      }}
                    >
                      {won ? (
                          <img
                            src="/v2/w.png"
                            alt="Win"
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: "contain",
                              display: "block",
                            }}
                          />
                        ) : (
                          <img
                            src="/v2/l.png"
                            alt="Loss"
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: "contain",
                              display: "block",
                            }}
                          />
                        )}
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            color: "#fff",
                            fontSize: 14,
                            lineHeight: "16px",
                            textShadow: "1px 1px #000",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getDefenseBattleLabel(log)}
                        </div>

                        <div
                          style={{
                            color: "#e7d649",
                            fontSize: 12,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Battle • {getLogDateLabel(log.createdAt)}
                        </div>
                      </div>

                      <div
                        className="shadow1"
                        style={{
                          color: won ? "#7CFC00" : "#ff8a8a",
                          fontSize: 15,
                          fontWeight: 800,
                        }}
                      >
                        {won ? "WIN" : "LOSS"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="modal_footer">
            <button onClick={() => setView("deck")} className="game-button green-button" style={{marginRight: 10}}>Battle Squad</button>
            <button onClick={() => onNavigate?.("inventoryHub")} className="game-button red-button">CLOSE</button>
          </div>
          </>
        )}

        {view === "deck" && (
          <>
            {!OVERALL_MODE && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 6,
                  marginBottom: 14,
                }}
              >
                {RARITIES.map((rarity) => {
                  const selected = activeRarity === rarity.key;
                  const count = selectedByRarity[rarity.key]?.length || 0;
                  const complete = count > 0;

                  return (
                    <button
                      key={rarity.key}
                      type="button"
                      onClick={() => {
                        setActiveRarity(rarity.key);
                        setError("");
                      }}
                      style={{
                        minHeight: 48,
                        border: selected
                          ? "2px solid #ffdf5f"
                          : "1px solid rgba(255,255,255,0.14)",
                        borderRadius: 12,
                        background: selected
                          ? "rgba(255,223,95,0.2)"
                          : "rgba(0,0,0,0.28)",
                        color: "#fff",
                        cursor: "pointer",
                        padding: 5,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: selected ? "#ffdf5f" : "#fff",
                          textShadow: "1px 1px #000",
                        }}
                      >
                        {rarity.label}
                      </div>

                      <div
                        style={{
                          fontSize: 11,
                          marginTop: 3,
                          color: complete ? "#7CFC00" : "rgba(255,255,255,0.65)",
                        }}
                      >
                        {count}/{deckSize}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="modal_inside_2">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <div>
                  <div
                  className="shadow1"
                    style={{
                      fontWeight: 600,
                      color: "#ffdf5f",
                      fontSize: 21,
                      textAlign: "left",
                    }}
                  >
                    {OVERALL_MODE
                      ? "Battle Squad"
                      : `${rarityLabel(activeRarity)} Defense`}
                  </div>

                <div
  className="shadow1"
  style={{
    fontSize: 13,
    marginTop: 1,
    fontWeight: 600,
    textAlign: "left",
    whiteSpace: "nowrap",
  }}
>
  Pick at least 1 card • Up to {deckSize}
  {isDirty ? " • Unsaved" : ""}
</div>
                </div>

                <button
                  type="button"
                  onClick={autoPickBest}
                  disabled={loading || saving}
                  className="game-button-small green-button-small"
                >
                  Auto Pick
                </button>
              </div>

             
<div
  style={{
    width: "100%",
    display: "grid",
    gridTemplateColumns: `repeat(${deckSize}, minmax(0, 1fr))`,
    gap: 8,
    boxSizing: "border-box",
  }}
>
  {Array.from({ length: deckSize }).map((_, idx) => (
   <BattleSquadSlot
  key={selectedCards[idx]?.id || `battle-slot-${idx}`}
  card={selectedCards[idx]}
  slotIndex={idx}
  onClick={openCardPickerForSlot}
  onRemove={removeCardFromSlot}
/>
  ))}
</div>

           
              {error && (
                <div
                  style={{
                    marginTop: 12,
                    color: "#ff8a8a",
                    fontSize: 14,
                    textAlign: "center",
                    textShadow: "1px 1px #000",
                  }}
                >
                  {error}
                </div>
              )}

              </div>

              <div className="modal_footer">

              <button
                  type="button"
                  onClick={saveDeck}
                  disabled={
                    saving ||
                    activeSelectedIds.length < 1 ||
                    activeSelectedIds.length > deckSize
                  }
                  style={{
                    marginRight: 10,
                    cursor:
                      saving ||
                      activeSelectedIds.length < 1 ||
                      activeSelectedIds.length > deckSize
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      saving ||
                      activeSelectedIds.length < 1 ||
                      activeSelectedIds.length > deckSize
                        ? 0.55
                        : 1,
                  }}
                  className="game-button green-button"
                >
                  {saving ? "Save" : "Save"}
                </button>

                <button
  type="button"
  className="game-button yellow-button" style={{marginRight: 10}}
  onClick={() => {
    setView("stats");
    loadDefenseStats();
  }}
>
  STATS
</button>

                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={saving || activeSelectedIds.length === 0}
                  style={{
                    cursor:
                      saving || activeSelectedIds.length === 0
                        ? "not-allowed"
                        : "pointer",
                    opacity: saving || activeSelectedIds.length === 0 ? 0.55 : 1,
                  }}
                  className="game-button red-button"
                >
                  CLEAR
                </button>

              </div>

              {activeSavedIds.length > 0 && (
                <button
                  type="button"
                  onClick={clearSavedDeck}
                  disabled={saving}
                  style={{
                    width: "calc(100% - 20px)",
                    margin: 10,
                    marginTop: 0,
                    height: 38,
                    border: "1px solid rgba(255,255,255,0.16)",
                    borderRadius: 10,
                    background: "rgba(0,0,0,0.2)",
                    color: "rgba(255,255,255,0.8)",
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                >
                  Remove Your Saved {OVERALL_MODE ? "" : rarityLabel(activeRarity)}{" "}
                  Deck
                </button>
              )}
          </>
        )}

        </>
  )}
      </motion.div>

      <AnimatePresence>
        {successOpen && (
          <div
            className="modal_box" style={{background: "transparent"}} onClick={() => setSuccessOpen(false)}
          >

            <div className="modal_title">Success
              <button className="modal_close" onClick={() => setSuccessOpen(false)}/>
            </div>
            
            <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

              <div className="modal_inside">
            
              {Array.from({ length: 16 }).map((_, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.3 }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0.3, 1.1, 0.8],
                    x: Math.cos((i / 16) * Math.PI * 2) * 140,
                    y: Math.sin((i / 16) * Math.PI * 2) * 140,
                  }}
                  transition={{
                    duration: 1.2,
                    delay: 0.05 * i,
                    ease: "easeOut",
                  }}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "linear-gradient(#fff,#ffd700)",
                    pointerEvents: "none",
                    zIndex: 0,
                  }}
                />
              ))}

              <img
                src="/finger.png"
                alt="Deck Saved"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                style={{
                  width: 78,
                  height: 78,
                  objectFit: "contain",
                  marginBottom: 10,
                  position: "relative",
                  zIndex: 1,
                  filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.55))",
                }}
              />

              <div className="shadow2"
                style={{
                  fontSize: 28, fontWeight: 600
                }}
              >
                Deck Saved
              </div>

              <p
              className="shadow1"
                style={{
                  fontSize: 21,
                  color: "#ddd",
                  fontWeight: 600,
                  margin: 0,
                  lineHeight: 1.45,
                  position: "relative",
                  zIndex: 1,
                  marginBottom: 10
                }}
              >
                Your{" "}
                <span style={{ color: "#09BDFF" }}>
                  {OVERALL_MODE ? "Battle Squad" : rarityLabel(activeRarity)}
                </span>{" "}
                is Ready!
              </p>

              <div className="info" style={{fontSize: 21, fontWeight: 600}}>
                {activeSelectedIds.length}/{deckSize} Saved
              </div>
              </div>

              <div className="modal_footer">
                <button
  className="game-button green-button"
  onClick={() => {
    setSuccessOpen(false);

    if (
      openedVia === "arena" ||
      returnTo === "arenas"
    ) {
      returnAfterDefenseDeck();
    }
  }}
>
  Awesome
</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {cardPickerOpen && (
  <div>
    <div className="modal_box">
      <div className="modal_title">
        Select Defense Card

        <button
          type="button"
          className="modal_close"
          onClick={closeCardPicker}
        />
      </div>

      <CardPicker
        openedVia="defenseDeck"
        onClose={closeCardPicker}
        onNavigate={onNavigate}
        onSelected={handlePickedDefenseCard}
      />
    </div>
  </div>
)}

    </div>
  );
}